import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';

const ConversationReview = () => {
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [roleFilter, setRoleFilter] = useState('');
  const [daysFilter, setDaysFilter] = useState(30);
  const [loadingStage, setLoadingStage] = useState('');
  const navigate = useNavigate();

  const fetchConversations = useCallback(async () => {
    let stageInterval = null;
    try {
      setLoading(true);
      setLoadingStage('Querying index...');
      const params = { days: daysFilter };
      if (roleFilter) params.role_filter = roleFilter;

      const stageMessages = ['Querying index...', 'Fetching results...', 'Rendering list...'];
      let stageIndex = 0;
      stageInterval = setInterval(() => {
        stageIndex = (stageIndex + 1) % stageMessages.length;
        setLoadingStage(stageMessages[stageIndex]);
      }, 900);
      
      const response = await api.get('/admin/conversations', { params });
      setLoadingStage('Rendering list...');
      setConversations(response.data.conversations || []);
      setError(null);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load conversations');
    } finally {
      if (stageInterval) {
        clearInterval(stageInterval);
      }
      setLoadingStage('');
      setLoading(false);
    }
  }, [roleFilter, daysFilter]);

  useEffect(() => {
    fetchConversations();
  }, []);

  const getIssueIcon = (count) => {
    if (count === 0) return '✅';
    if (count <= 2) return '⚠️';
    return '🚨';
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
    const diffDays = Math.floor(diffHours / 24);

    if (diffHours < 1) return 'Just now';
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
    return date.toLocaleDateString();
  };

  if (loading && conversations.length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-sm text-gray-600">{loadingStage || 'Loading...'}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      {/* Header */}
      <div className="mb-8 flex items-center justify-between">
        <div>
          <div className="flex items-center space-x-3">
            <button
              onClick={() => navigate('/admin')}
              className="text-blue-600 hover:text-blue-700 font-medium"
            >
              ← Back to Admin
            </button>
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mt-3">Conversation Review Dashboard</h1>
          <p className="mt-2 text-sm text-gray-600">
            Review and analyze conversations to identify and fix issues
          </p>
        </div>
        <button
          onClick={() => navigate('/dashboard')}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
        >
          Back to Chat
        </button>
      </div>

      {/* Filters */}
      <div className="bg-white shadow rounded-lg p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              User Role
            </label>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value="">All Roles</option>
              <option value="salesperson">Salesperson</option>
              <option value="account_manager">Account Manager</option>
              <option value="admin">Admin</option>
            </select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Time Period
            </label>
            <select
              value={daysFilter}
              onChange={(e) => setDaysFilter(Number(e.target.value))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              <option value={1}>Last 24 hours</option>
              <option value={7}>Last 7 days</option>
              <option value={30}>Last 30 days</option>
              <option value={90}>Last 90 days</option>
            </select>
          </div>

          <div className="flex items-end">
            <button
              onClick={fetchConversations}
              className="w-full px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              Refresh
            </button>
          </div>
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6">
          {error}
        </div>
      )}

      {loading && conversations.length > 0 && (
        <div className="bg-blue-50 border border-blue-200 text-blue-700 px-4 py-3 rounded-lg mb-6 text-sm">
          {loadingStage || 'Refreshing conversations...'}
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
        <div className="bg-white shadow rounded-lg p-6">
          <div className="text-sm font-medium text-gray-500">Total Conversations</div>
          <div className="mt-2 text-3xl font-bold text-gray-900">{conversations.length}</div>
        </div>
        <div className="bg-white shadow rounded-lg p-6">
          <div className="text-sm font-medium text-gray-500">With Issues</div>
          <div className="mt-2 text-3xl font-bold text-orange-600">
            {conversations.filter(c => c.mentioned_sites_count > 50).length}
          </div>
        </div>
        <div className="bg-white shadow rounded-lg p-6">
          <div className="text-sm font-medium text-gray-500">Avg Messages</div>
          <div className="mt-2 text-3xl font-bold text-gray-900">
            {conversations.length > 0 
              ? Math.round(conversations.reduce((sum, c) => sum + c.message_count, 0) / conversations.length)
              : 0}
          </div>
        </div>
        <div className="bg-white shadow rounded-lg p-6">
          <div className="text-sm font-medium text-gray-500">Avg Sites</div>
          <div className="mt-2 text-3xl font-bold text-gray-900">
            {conversations.length > 0
              ? Math.round(conversations.reduce((sum, c) => sum + (c.mentioned_sites_count || 0), 0) / conversations.length)
              : 0}
          </div>
        </div>
      </div>

      {/* Conversations List */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Conversations ({conversations.length})
          </h2>
        </div>

        {conversations.length === 0 ? (
          <div className="px-6 py-12 text-center text-gray-500">
            No conversations found for the selected filters
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {conversations.map((conv) => (
              <div
                key={conv.conversation_id}
                onClick={() => navigate(`/admin/conversation/${conv.conversation_id}`)}
                className="px-6 py-4 hover:bg-gray-50 cursor-pointer transition-colors"
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <span className="text-2xl">
                        {getIssueIcon(conv.mentioned_sites_count > 100 ? 1 : 0)}
                      </span>
                      <div>
                        <h3 className="text-sm font-medium text-gray-900">
                          {conv.user_name} - {conv.vertical || 'General'} Sites
                          {conv.num_sites && ` (${conv.num_sites} requested)`}
                        </h3>
                        <div className="mt-1 flex items-center space-x-4 text-xs text-gray-500">
                          <span className="inline-flex items-center">
                            <span className="font-medium">{conv.message_count}</span>
                            <span className="ml-1">messages</span>
                          </span>
                          <span className="inline-flex items-center">
                            <span className="font-medium">{conv.mentioned_sites_count || 0}</span>
                            <span className="ml-1">sites mentioned</span>
                          </span>
                          <span>{formatDate(conv.updated_at)}</span>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="ml-4 flex-shrink-0">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      conv.user_role === 'salesperson' 
                        ? 'bg-blue-100 text-blue-800'
                        : conv.user_role === 'account_manager'
                        ? 'bg-green-100 text-green-800'
                        : 'bg-purple-100 text-purple-800'
                    }`}>
                      {conv.user_role?.replace('_', ' ')}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ConversationReview;
